import React, { useEffect, useState } from "react";
import championIcons from "./championIcons";
import Markdown from "react-markdown";
import "./index.css";

function sleep(ms: number) {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

interface UserData {
  gamename: string;
  tagline: string;
}

interface SummonerResponse {
  me: {
    riotId: string;
    championId: number;
    championName: string;
  };
  opponent: {
    riotId: string;
    championId: number;
    championName: string;
  };
  lane: string;
}

const GameMatchupPage: React.FC = () => {
  const [currentState, setCurrentState] = useState<
    "input" | "loading" | "display" | "error"
  >("input");
  const [gamename, setGamename] = useState(
    localStorage.getItem("gamename") || ""
  );
  const [tagline, setTagline] = useState(localStorage.getItem("tagline") || "");
  const [matchup, setMatchup] = useState<SummonerResponse | null>(null);
  const [insights, setInsights] = useState<any>(null);
  const [insightStatus, setInsightStatus] = useState<string>("pending");
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (gamename && tagline) {
      console.log("gamename", gamename);
      console.log("tagline", tagline);
      handleConfirm();
    }
  }, []);

  const handleConfirm = async () => {
    if (!gamename.trim() || !tagline.trim()) return;
    setCurrentState("loading");
    setError(null);

    try {
      const url = `https://lol-lobby-api.seanwessmith.workers.dev/api/summoner/${encodeURIComponent(
        gamename
      )}/${encodeURIComponent(tagline)}`;
      const resp = await fetch(url);
      if (!resp.ok) throw new Error("Summoner not found");
      const data: SummonerResponse = await resp.json();
      setMatchup(data);
      setCurrentState("display");
      localStorage.setItem("gamename", gamename);
      localStorage.setItem("tagline", tagline);
    } catch (err) {
      setError(
        "Could not fetch matchup. Please check your info and try again."
      );
      setCurrentState("error");
    }
  };

  // keep fetching insights in background until ready
  useEffect(() => {
    const asyncEffect = async () => {
      while (insightStatus === "pending") {
        await sleep(5000);
        await fetch(
          `https://lol-lobby-api.seanwessmith.workers.dev/api/insights/${matchup?.me.championName}/${matchup?.opponent.championName}`
        )
          .then((res) => res.json())
          .then((data) => {
            console.log("insights", data);
            setInsights(data.matchupInsights);
            setInsightStatus(data.matchupInsights.status);
          });
      }
    };
    if (insightStatus === "pending" && matchup) {
      asyncEffect();
    }
  }, [insightStatus, matchup]);

  const handleReset = () => {
    setCurrentState("input");
    setGamename("");
    setTagline("");
    setMatchup(null);
    setError(null);
  };

  // Input state
  if (currentState === "input") {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-900 to-gray-800 flex items-center justify-center p-4">
        <div className="bg-gray-800 rounded-lg shadow-2xl p-8 w-full max-w-md border border-gray-700">
          <h1 className="text-3xl font-bold text-white mb-8 text-center">
            Enter Your Info
          </h1>
          <div className="space-y-6">
            <div>
              <label
                htmlFor="gamename"
                className="block text-sm font-medium text-gray-300 mb-2"
              >
                Game Name
              </label>
              <input
                id="gamename"
                type="text"
                value={gamename}
                onChange={(e) => setGamename(e.target.value)}
                className="w-full px-4 py-2 bg-gray-700 border border-gray-600 rounded-md text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="Enter your game name"
              />
            </div>
            <div>
              <label
                htmlFor="tagline"
                className="block text-sm font-medium text-gray-300 mb-2"
              >
                Tagline
              </label>
              <input
                id="tagline"
                type="text"
                value={tagline}
                onChange={(e) => setTagline(e.target.value)}
                className="w-full px-4 py-2 bg-gray-700 border border-gray-600 rounded-md text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="Enter your tagline (e.g., NA1)"
              />
            </div>
            <button
              onClick={handleConfirm}
              disabled={!gamename.trim() || !tagline.trim()}
              className="w-full py-3 px-4 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-600 disabled:cursor-not-allowed text-white font-semibold rounded-md transition duration-200 ease-in-out transform hover:scale-105 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 focus:ring-offset-gray-800"
            >
              Confirm
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Loading state
  if (currentState === "loading") {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-900 text-white">
        <div className="text-xl animate-pulse">Loading match info...</div>
      </div>
    );
  }

  // Error state
  if (currentState === "error") {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-900 text-white">
        <div className="bg-red-800 rounded-lg p-8 shadow-2xl">
          <div className="text-2xl font-bold mb-4">Error</div>
          <div className="mb-6">{error}</div>
          <button
            onClick={handleReset}
            className="bg-gray-700 px-4 py-2 rounded hover:bg-gray-600"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  // Display state, using real fetched data
  if (matchup?.me && matchup.opponent && currentState === "display") {
    const meName = matchup.me.riotId?.replace("#", "#");
    const opponentName = matchup.opponent.riotId.replace("#", "#");

    const userChampion = matchup.me.championName;
    const opponentChampion = matchup.opponent.championName;

    const meSplash = `https://ddragon.leagueoflegends.com/cdn/img/champion/loading/${userChampion}_0.jpg`;
    const opponentSplash = `https://ddragon.leagueoflegends.com/cdn/img/champion/loading/${opponentChampion}_0.jpg`;

    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-900 to-gray-800 flex justify-center p-4">
        <div className="bg-gray-800 rounded-lg shadow-2xl p-8 w-full max-w-4xl border border-gray-700">
          <div className="flex flex-col justify-between items-center">
            <h1 className="text-4xl font-bold text-white text-center">
              Match Preview
            </h1>
            <h3 className="text-2xl text-blue-300 mb-8">{matchup.lane} Lane</h3>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {/* User Side */}
            <div
              className="relative rounded-lg overflow-hidden border border-blue-700"
              style={{
                backgroundImage: `url(${meSplash})`,
                backgroundSize: "cover",
                backgroundPosition: "center",
              }}
            >
              {/* gradient overlay */}
              <div className="absolute inset-0 bg-gradient-to-br opacity-50" />

              {/* content on top */}
              <div className="relative p-6 text-center">
                <h2 className="text-lg font-semibold text-blue-300 mb-2">
                  You
                </h2>
                <div className="text-2xl font-bold text-white mb-4">
                  {meName}
                </div>
                <div className="mt-4">
                  <p className="text-sm text-blue-300 mb-1">Champion</p>
                  <div className="flex flex-row text-xl font-semibold text-white">
                    <img
                      src={championIcons[matchup.me.championId]}
                      className="w-8 h-8 ml-2"
                    />
                    {userChampion}
                  </div>
                </div>
              </div>
            </div>

            {/* Opponent Side */}
            <div
              className="relative rounded-lg overflow-hidden border border-red-700"
              style={{
                backgroundImage: `url(${opponentSplash})`,
                backgroundSize: "cover",
                backgroundPosition: "center",
              }}
            >
              {/* gradient overlay */}
              <div className="absolute inset-0 bg-gradient-to-br opacity-50" />

              {/* content on top */}
              <div className="relative p-6 text-center">
                <h2 className="text-lg font-semibold text-red-300 mb-2">
                  Opponent
                </h2>
                <div className="text-2xl font-bold text-white mb-4">
                  {opponentName}
                </div>
                <div className="mt-4">
                  <p className="text-sm text-red-300 mb-1">Champion</p>
                  <div className="flex flex-row text-xl font-semibold text-white">
                    <img
                      src={championIcons[matchup.opponent.championId]}
                      className="w-8 h-8 ml-2"
                    />
                    {opponentChampion}
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div className="mt-8 text-center">
            {insightStatus === "pending" && (
              <div className="text-center">
                <div className="text-3xl font-bold text-yellow-400 mb-6">
                  Loading...
                </div>
              </div>
            )}
            {insights && insights.status === "ready" && (
              <div className="text-white">
                <h2 className="text-2xl font-bold mb-4">Matchup Insights</h2>
                {/* display insights.result as markdown */}
                <div className="flex flex-row gap-2">
                  <Markdown>{JSON.parse(insights.result).champA}</Markdown>
                  <Markdown>{JSON.parse(insights.result).champB}</Markdown>
                </div>
                <Markdown>{JSON.parse(insights.result).summary}</Markdown>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  // Fallback
  return null;
};

export default GameMatchupPage;
